import { signal } from '@preact/signals'
import type { Signal } from '@preact/signals'
import { storeSubtitlePreference, storedSubtitlePreference } from './subtitles'
import type { SubtitlePreference, SubtitleStyle } from './subtitles'

/**
 * One choice per page, shared by every player on it, so turning subtitles on in the live picture shows them in the
 * rehearsal too. Read from storage the first time a player asks, not as the bundle loads.
 */
let shared: Signal<SubtitlePreference> | null = null

function browserStorage(): Storage | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage
    } catch {
        return null
    }
}

function preference(): Signal<SubtitlePreference> {
    shared ??= signal(storedSubtitlePreference(browserStorage()))
    return shared
}

/** Forgets the page's choice, so the next player reads storage again. For tests. */
export function forgetSubtitlePreference(): void {
    shared = null
}

export interface SubtitlePreferenceState extends SubtitlePreference {
    setOn: (on: boolean) => void
    setStyle: (style: SubtitleStyle) => void
}

/** The viewer's choice, and how to change it; each change is kept for the next visit. */
export function useSubtitlePreference(): SubtitlePreferenceState {
    const current = preference()
    const set = (next: SubtitlePreference) => {
        current.value = next
        storeSubtitlePreference(browserStorage(), next)
    }
    return {
        ...current.value,
        setOn: on => set({ ...current.value, on }),
        setStyle: style => set({ ...current.value, style }),
    }
}
